import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, AfterViewInit, Optional, ElementRef, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent, MatDatepickerInput } from '@angular/material/datepicker';
import { CUSTOM_DATE_FORMATS } from '@shared/core/modules/material';
import * as moment from 'moment';

import * as Utils from '@shared/core/utils';

import { IInputFieldInit } from './forms-input.shared.interface';

import { Subscription } from 'rxjs';

@Component({
    selector: 'shared-forms-input',
    templateUrl: './forms-input.shared.component.html',
    styleUrls: ['./forms-input.shared.component.scss'],
})
export class FormsInputComponent implements OnInit, OnDestroy, AfterViewInit {
    private _init: boolean = false;
    private _previousValue!: string;
    public elem!: HTMLElement;
    public inputElem!: HTMLInputElement | HTMLTextAreaElement;
    public selectIsOpen: boolean = false;
    public selected: OLO.Components.DropDown.IDropDownOption;
    public isFocusing: boolean = false;
    public hasBlurred: boolean = false;
    public control!: AbstractControl;
    public modelClasses!: { [key: string]: any; };

    @Input() public showHint: boolean = true;
    @Input() public parentForm: FormGroup;
    @Input() public type: 'textarea' | 'text' | 'password' | 'number' | 'email' | 'checkbox' | 'radio' | 'select' | 'datePicker' = 'text';
    @Input() public inputmode: string;
    @Input() public placeholder: string = '';
    @Input() public label: string;
    @Input() public disabled: boolean = false;
    @Input() public valid: boolean = false;
    @Input() public errors: any = false;
    @Input() public name: string = '';
    @Input() public value: string = '';
    @Input() public isTouched: boolean = false;
    @Input() public resize: boolean = false;
    @Input() public icon: string = 'checkmark';
    @Input() public tabindex: number = 0;
    @Input() public required: boolean = true;
    @Input() public optionalLabel: string = ' ( optional )';
    @Input() public pattern: RegExp = null;
    @Input() public readonly: boolean = null;
    @Input() public min: number = null;
    @Input() public max: number = null;
    @Input() public minLength: number = null;
    @Input() public maxLength: number = null;
    @Input() public novalidate: boolean = true;
    @Input() public autocomplete: boolean = false;
    @Input() public log: boolean = false;
    @Input() public compact: boolean = true; /* Keep bottom margins low - for modal views */
    @Input() public numbersOnly: boolean = false;
    @Input() public enableClear: boolean = false;
    @Input() public inputSize: OLO.Components.INPUT_SIZE = 'none';
    @Input() public inputCenter: boolean = false;

    /* Date picker type */
    @Input() public startDate: Date = Utils.Dates.subtractYearsFromToday(16 + 10);
    @Input() public maxDate: Date = Utils.Dates.subtractYearsFromToday(16);
    @Input() public minDate: Date = new Date(new Date().setFullYear(1900));
    @Input() public smallScreen: number = 1000;
    @Input() public format: string = null;
    @ViewChild('datePicker') public datePicker!: MatDatepicker<HTMLElement>;

    @Input() public hintsHSide: 'left' | 'right' | 'center' = 'right';
    @Input() public hintsVSide: 'top' | 'bottom' | 'center' = 'bottom';
    @Input() public hintsFeel: 'warning' | 'danger' = 'danger';
    @Input() public hintsSize: OLO.Components.FORM_HINTS_SIZE = null;

    @Output() public onChange: EventEmitter<OLO.Components.IPrevNextEvent> = new EventEmitter();
    @Output() public onStatusChange: EventEmitter<string> = new EventEmitter();
    @Output() public onFocus: EventEmitter<Event> = new EventEmitter();
    @Output() public onInit: EventEmitter<IInputFieldInit> = new EventEmitter();
    @Output() public onButtonClick: EventEmitter<Event> = new EventEmitter();
    @Output() public onSelectChange: EventEmitter<OLO.Components.DropDown.IDropDownOption> = new EventEmitter();

    @Input() public theme: OLO.Components.DropDown.IDropDownTheme = 'light-2';
    @Input() public position: OLO.Components.DropDown.IDropDownPosition = 'bl';
    @Input() public selectWidth: OLO.Components.DROPDOWN_WIDTH = 'full';
    @Input() public projection: 'local' | 'global' = 'global';
    @Input() public selectDropdownSize: OLO.Components.DropDown.IDropDownSize = 'lg';
    @Input() public optionTemplate: TemplateRef<any> = null;
    @Input() public selectedTemplate: TemplateRef<any> = null;
    @Input() public selectIconSize: OLO.Components.BUTTON_SIZE = 'xxs';
    @Input() public onSelectUpdateRevalidatedFields: string[] = null;
    @Input() public defaultSelected!: number;
    @Input() public selectOptions: OLO.Components.DropDown.IDropDownOption[] = [];
    @ViewChild('selectButton') public selectElementRef!: ElementRef;

    private _valueChangesListener$!: Subscription;
    private _statusChangesListener$!: Subscription;

    constructor(
        @Optional() public elementRef: ElementRef,
        @Optional() public changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        if (this.parentForm && !this.name) {
            return console.warn(`"name" property not provided for Input Field Component. When you provide
            "parentForm" prop, "name" is required. Otherwise control will be in template driven instead of model driven`);
        }

        if (this.parentForm && this.name) {
            this.control = this.parentForm.controls[this.name];

            if (!this.control) {
                console.log(`Unable to find input control for name "${this.name}" in form:`, this.parentForm, 'Check if field is defined in component.');

                return;
            }

            /* Overwrite required property - will check manually if field is required by validators */
            const validator = this.control.validator ? this.control.validator(({} as AbstractControl)) : null;
            if (validator === null || validator.required !== true) {
                this.required = false;
            }
            if (validator !== null && validator.required === true) {
                this.required = true;
            }


            this._valueChangesListener$ = this.control.valueChanges.subscribe(value => {
                if (this.log) {
                    console.warn(`[subscribeValueChanges] input field "${this.name}"`, value, this.control);
                }
                this.handleValueChange(value);
            });

            this._statusChangesListener$ = this.control.statusChanges.subscribe(status => {
                if (this.log) {
                    console.warn(`[subscribeStatusChanges] input field "${this.name}"`, status, this.control);
                }
                this.handleStatusChange(status);
            });

            if (this.log) {
                console.warn(`[init] input field "${this.name}"`, this.control);
            }
        }

        /* Set default placeholder for date picker */
        if (this.type === 'datePicker') {
            if (!this.placeholder) {
                if (this.format) {
                    this.placeholder = this.format;
                } else {
                    this.placeholder = CUSTOM_DATE_FORMATS.display.dateInput;
                }
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.elementRef) {
            this.elem = this.elementRef.nativeElement;
            this.inputElem = this.elem.querySelector(this.type === 'textarea' ? 'textarea' : 'input');
        }

        setTimeout(() => {
            this._init = true;

            const emitValue: IInputFieldInit = {
                formControl: this.control || null,
                parentElement: this.elem || null,
                inputElement: this.inputElem || null,
            };

            if (this.log) {
                console.warn(`[onInit] input field "${this.name}"`, emitValue, this.control);
            }

            if (this.type !== 'select') {
                this.onInit.emit(emitValue);
                this._previousValue = this.inputElem && this.inputElem.value || null;
            }

            /* Select default value */
            if (this.type === 'select') {
                this._updateDefaultSelectValue();
                if (this.selectElementRef && this.selectElementRef.nativeElement) {
                    /* Disable button inside select to prevent from form being submitted */
                    this.selectElementRef.nativeElement.onclick = e => e.preventDefault();
                }

                /* Default select value */
                if (this.control?.value && this.selectOptions?.length) {
                    this.selected = this.selectOptions.find(obj => `${obj.Id}` === `${this.control.value}`);
                    if (this.selected) {
                        this.onSelected(this.selected);
                    }
                }
            }
        }, 0);
    }

    ngOnDestroy(): void {
        if (this._valueChangesListener$ && !this._valueChangesListener$.closed) this._valueChangesListener$.unsubscribe();
        if (this._statusChangesListener$ && !this._statusChangesListener$.closed) this._statusChangesListener$.unsubscribe();
    }

    private _formatVisibleDate(): void {
        if (this.type === 'datePicker' && this.control && this.control.value && this.inputElem && this.format) {
            const d: moment.Moment = this.control.value;
            this.inputElem.value = d.format(this.format);
        }
    }

    private _updateDefaultSelectValue(): void {
        if (this.type === 'select') {
            if (this.defaultSelected !== undefined && typeof this.defaultSelected === 'number' && !this.selected && this.selectOptions && this.selectOptions.length) {
                this.selected = this.selectOptions.find(obj => obj.Id === this.defaultSelected);

                if (this.selected && this.control) {
                    this.control.setValue(this.selected.Id);
                }
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    private async _blurDatePickerInput(): Promise<boolean> {
        return new Promise(resolve => {
            setTimeout(() => {
                this.changeDetectorRef.detectChanges();
                if (this.inputElem && this.type === 'datePicker') {
                    this.inputElem.blur();
                    this.hasBlurred = true;

                    resolve(true);
                } else {
                    resolve(false);
                }
            }, 0);
        });
    }

    public onSelectToggle(isOpen: boolean): void {
        if (this.disabled) {
            this.selectIsOpen = false;
            this.changeDetectorRef.detectChanges();

            return;
        }
        this.selectIsOpen = isOpen;
        this.changeDetectorRef.detectChanges();
    }

    public onSelected($event: OLO.Components.DropDown.IDropDownOption): void {
        this.selected = $event;
        if (this.control) {
            this.control.setValue(this.selected.Id);
            this.control.markAsDirty();
            this.control.markAsTouched();
        }

        if (this.onSelectUpdateRevalidatedFields && this.onSelectUpdateRevalidatedFields.length) {
            this.onSelectUpdateRevalidatedFields.forEach(fieldName => {
                const abstractControl: AbstractControl = Utils.ValidatorsUtils.findControl(fieldName, this.control);
                if (abstractControl) {
                    abstractControl.updateValueAndValidity();
                }
            });
        }

        if (!this.control) {
            this.onSelectChange.emit($event);
        }
        this.changeDetectorRef.detectChanges();
    }

    public handleDateChange($event: MatDatepickerInputEvent<MatDatepickerInput<MatDatepicker<HTMLElement>>>): void {
        this.handleValueChange($event.value);

        this._blurDatePickerInput();
        this.changeDetectorRef.detectChanges();
    }

    public handleValueChange(value: any): void {
        if (this.log) {
            console.warn(`[onChange] input field "${this.name}"`, value, this.control);
        }
        this.onChange.emit({ previous: this._previousValue, next: value });
        this._previousValue = value;

        if (this.type === 'select') {
            if (!this.selected || this.selected.Id !== value) {
                setTimeout(() => {
                    const foundOption = this.selectOptions?.find(option => option.Id === value);
                    if (foundOption) {
                        this.selected = foundOption;
                    }
                    this.changeDetectorRef.detectChanges();
                }, 0);
            }

        }

        this._formatVisibleDate();

        this.changeDetectorRef.detectChanges();
    }

    public handleStatusChange(status: string): void {
        if (this.log) {
            console.warn(`[onStatusChange] input field "${this.name}"`, status, this.control);
        }

        this.onStatusChange.emit(status);
        this.changeDetectorRef.detectChanges();
    }

    public handleOnFocus($event: Event): void {
        this.isTouched = true;
        this.isFocusing = true;

        if (this.log) {
            console.warn(`[onFocus] input field "${this.name}"`, ' isTouched:', this.isTouched, ' isFocusing:', this.isFocusing, this.control);
        }

        this._formatVisibleDate();
        this.onFocus.emit($event);

        this.changeDetectorRef.detectChanges();
    }

    public handleOnClick($event: Event): void {
        if (this.type === 'datePicker') {
            if (this.datePicker.opened) {
                this.datePicker.close();
                this._blurDatePickerInput();
            } else {
                this.datePicker.open();
            }
        }

        this._formatVisibleDate();

        this.changeDetectorRef.detectChanges();
    }

    public handleOnBlur($event: Event): void {
        this.isFocusing = false;
        this.hasBlurred = true;

        if (this.log) {
            console.warn(`[onFocus/blur] input field "${this.name}"`, ' isFocusing:', this.isFocusing, this.control);
        }
        this.onFocus.emit($event);

        this._formatVisibleDate();

        this.changeDetectorRef.detectChanges();
    }

    public handleButtonClick($event: Event): void {
        this.onButtonClick.emit($event);

        this.changeDetectorRef.detectChanges();
    }

    public handleClearInputValue($event: void) {
        this.control?.reset();
    }

    public get indicatorStatus(): OLO.Components.MESSAGES_TYPE {
        switch (true) {
            case this.control.status === 'DISABLED' && !this.errors && this.control.disabled === true:
                return 'disabled';
            case this.control.status === 'PENDING':
                return 'loading';
            case this.control.status === 'VALID' && !this.errors:
                return 'success';
            case this.control.status === 'INVALID' || this.errors !== null:
                return 'danger';
            default:
                return null;
        }
    }

    public get isSmallScreen(): boolean {
        return this.smallScreen ? window.innerWidth < this.smallScreen : false;
    }

    public get showHintsComponent(): boolean {
        if (this.type === 'datePicker') {
            return this.control !== undefined
                && this.control.touched === true
                && this.hasBlurred
                && this.datePicker.opened === false
                && this.showHint === true;
        }

        return this.control !== undefined && (this.control.touched === true || this.control.dirty === true) && this.showHint === true;
    }

    public get hasErrors(): boolean {
        if (this.control) {
            const commonErrors: boolean = (this.control.touched === true || this.control.dirty === true) && (this.control.errors || this.errors || this.control.invalid);

            return commonErrors;
        } else {
            const commonErrors: boolean = !!this.errors === true;

            return commonErrors;
        }
    }

    public get hasValue(): boolean {
        return this.control?.value !== '';
    }

}
